import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../../redux/app';
import { ExchangeRateCalculator, MTProfileCalculator } from '@vt/vt-trading-utils';
import { makePositionNoReduceSelector, makeSymbolKeySelector } from '../../../redux/selector';
import {
  setMultiplePositionProfits,
  setCalculationStatus,
  selectCalculationCount,
  removePositionProfit,
  selectAllPositionProfits,
  clearAllProfits
} from '../../../redux/slices/positionProfitsSlice';

/**
 * Background component that calculates profits for all positions
 * Uses calculator.calculateProfit for accurate calculation
 * Uses throttle to ensure calculation happens even when tick updates rapidly
 */
const PositionBackgroundCalculator = () => {
  const dispatch = useAppDispatch();

  // Get data from Redux
  const tick = useAppSelector((state) => state?.stat?.tick);
  const getPositionState = useMemo(makePositionNoReduceSelector, []);
  const positions = useAppSelector((state) => getPositionState(state));
  const calculationCount = useAppSelector(selectCalculationCount);
  const currentProfits = useAppSelector(selectAllPositionProfits);

  // Create calculator instances
  const symbolKey = useAppSelector(useMemo(makeSymbolKeySelector, []));
  const calculator = useMemo(() => new MTProfileCalculator(symbolKey), [symbolKey]);
  const exchangerate = useMemo(() => new ExchangeRateCalculator(tick), [tick]);

  // Ref to track previous calculation to prevent unnecessary updates
  const lastCalculationRef = useRef({});
  const calculationTimeoutRef = useRef(null);
  const lastExecutionTimeRef = useRef(0);
  const isCalculatingRef = useRef(false);

  // Helper function to generate position ID
  const getPositionId = useCallback((position) => {
    return `${position.symbol}_${position.priceOpen}_${position.action}_${position.volume}_${position.position}`;
  }, []);

  // Helper function to compare if calculation is needed
  const shouldRecalculate = useCallback((positionId, currentPrice, rate) => {
    const lastCalc = lastCalculationRef.current[positionId];
    if (!lastCalc) return true;

    // Check if price or rate changed significantly
    const priceChanged = Math.abs(lastCalc.currentPrice - currentPrice) > 0.00001;
    const rateChanged =
      Math.abs((lastCalc.rate?.bid || 1) - (rate?.bid || 1)) > 0.00001 ||
      Math.abs((lastCalc.rate?.ask || 1) - (rate?.ask || 1)) > 0.00001;

    return priceChanged || rateChanged;
  }, []);

  // Main calculation function - stable version without frequently changing dependencies
  const calculateAllPositionProfits = useCallback(() => {
    // Prevent concurrent calculations
    if (isCalculatingRef.current) {
      return;
    }

    const currentProfitsSnapshot = currentProfits;
    
    if (!positions || positions.length === 0) {
      // If no positions exist, clear all profits
      if (currentProfitsSnapshot && Object.keys(currentProfitsSnapshot).length > 0) {
        // console.log('🧹 No positions found, clearing all profits');
        dispatch(clearAllProfits());
        // Also clear calculation tracking
        lastCalculationRef.current = {};
      }
      return;
    }

    if (!tick || !exchangerate || !calculator) {
      return;
    }

    isCalculatingRef.current = true;
    dispatch(setCalculationStatus(true));

    const profits = {};
    let calculatedCount = 0;

    try {
      // Process each position
      for (const position of positions) {
        if (!position || !tick[position.symbol]) continue;

        const positionId = getPositionId(position);
        const currentPrice =
          position.action === 0 ? tick[position.symbol]?.bid : tick[position.symbol]?.ask;

        // Get exchange rate for this position
        const rateResult = exchangerate.getSymbolRateByQoute(symbolKey[position.symbol].quote);
        const rate = {
          bid: rateResult?.rate?.bid || 1,
          ask: rateResult?.rate?.ask || 1,
          quote: rateResult?.quote || 'USD'
        };

        // Check if recalculation is needed
        if (!shouldRecalculate(positionId, currentPrice, rate)) {
          continue;
        }

        let profit = 0;

        try {
          const profitResult = calculator.calculateProfit({
            symbol: position.symbol,
            orderType: position.action === 0 ? 'BUY' : 'SELL',
            openPrice: position.priceOpen,
            currentPrice: currentPrice,
            profitCurrency: symbolKey[position.symbol].quote,
            volume: position.volume / 10000.0,
            accountCurrency: 'USD',
            exchange: rate
          });

          profit = profitResult?.profit || 0;
          calculatedCount++;

          // Debug log for individual calculation in development
          // if (process.env.NODE_ENV === 'development') {
          //   console.log(`📈 Calculated ${position.symbol}: ${profit.toFixed(2)} (${position.action === 0 ? 'BUY' : 'SELL'})`);
          // }
        } catch (calcError) {
          console.warn(`Calculation failed for ${position.symbol}:`, calcError);
          profit = 0;
        }

        // Store the calculated profit
        profits[positionId] = profit;

        // Update calculation tracking
        lastCalculationRef.current[positionId] = {
          currentPrice,
          rate,
          timestamp: Date.now()
        };
      }

      // Update Redux store with all calculated profits
      if (Object.keys(profits).length > 0) {
        dispatch(
          setMultiplePositionProfits({
            profits,
            calculatedBy: 'Background Calculator',
          })
        );

        // Debug log in development
        // if (process.env.NODE_ENV === 'development') {
        //   console.log(
        //     `🔢 Background calculated ${calculatedCount} positions using Calculator`
        //   );
        // }
      }
    } catch (error) {
      console.error('Background position calculation error:', error);
    } finally {
      isCalculatingRef.current = false;
      dispatch(setCalculationStatus(false));
    }
  }, [
    positions,
    tick,
    exchangerate,
    calculator,
    dispatch,
    getPositionId,
    shouldRecalculate,
    symbolKey,
    currentProfits
  ]);

  // Throttled calculation effect - ensures calculation happens even when tick updates rapidly
  useEffect(() => {
    const now = Date.now();
    const timeSinceLastExecution = now - lastExecutionTimeRef.current;
    const THROTTLE_DELAY = 0; // 300ms throttle - guaranteed to execute at least every 300ms

    // Clear previous timeout
    if (calculationTimeoutRef.current) {
      clearTimeout(calculationTimeoutRef.current);
    }

    // If enough time has passed, execute immediately
    if (timeSinceLastExecution >= THROTTLE_DELAY) {
      lastExecutionTimeRef.current = now;
      calculateAllPositionProfits();
    } else {
      // Otherwise, schedule execution for the remaining time
      const remainingTime = THROTTLE_DELAY - timeSinceLastExecution;
      calculationTimeoutRef.current = setTimeout(() => {
        lastExecutionTimeRef.current = Date.now();
        calculateAllPositionProfits();
      }, remainingTime);
    }

    // Cleanup timeout on unmount
    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, [calculateAllPositionProfits]);

  // Cleanup profits for positions that no longer exist
  useEffect(() => {
    if (!positions || positions.length === 0 || !currentProfits) return;

    // Get current position IDs
    const currentPositionIds = new Set(
      positions.map(position => getPositionId(position)).filter(Boolean)
    );

    // Find profits that don't have corresponding positions
    const profitIds = Object.keys(currentProfits);
    const orphanedProfitIds = profitIds.filter(profitId => !currentPositionIds.has(profitId));

    // Remove orphaned profits
    if (orphanedProfitIds.length > 0) {
      // console.log(`🧹 Cleaning up ${orphanedProfitIds.length} orphaned profits:`, orphanedProfitIds);
      orphanedProfitIds.forEach(profitId => {
        dispatch(removePositionProfit({ positionId: profitId }));
      });

      // Also cleanup lastCalculationRef
      orphanedProfitIds.forEach(profitId => {
        delete lastCalculationRef.current[profitId];
      });
    }
  }, [positions, currentProfits, dispatch, getPositionId]);

  // Debug info in development
  // useEffect(() => {
  //   if (process.env.NODE_ENV === 'development') {
  //     console.log(
  //       `📊 PositionBackgroundCalculator: Tracking ${positions?.length || 0} positions, Calculator ready: ${!!calculator}, ExchangeRate ready: ${!!exchangerate}`
  //     );
  //   }
  // }, [positions?.length, calculator, exchangerate]);

  // This component doesn't render anything visible
  return null;
};

export default PositionBackgroundCalculator;
